// src/components/users/UserStatsCards.tsx
// Statistics cards for user overview

import React from 'react';
import { Users, UserCheck, UserX, Shield, TrendingUp, Car } from 'lucide-react';
import { UserStats } from '../../types/user';
import './UserStatsCards.css';

interface UserStatsCardsProps {
  stats: UserStats | null;
}

const UserStatsCards: React.FC<UserStatsCardsProps> = ({ stats }) => {
  // Handle loading/null state - MUST BE FIRST
  if (!stats) {
    return (
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#94a3b8' }}>
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">Loading...</div>
            <div className="stat-label">Statistics</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-grid">
      {/* Total Users */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#3b82f6' }}>
          <Users size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.overview.totalUsers.toLocaleString()}</div>
          <div className="stat-label">Total Users</div>
        </div>
      </div>

      {/* Active Users */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#10b981' }}>
          <UserCheck size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.overview.activeUsers.toLocaleString()}</div>
          <div className="stat-label">Active Users</div>
        </div>
      </div>

      {/* Verified Users */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#8b5cf6' }}>
          <Shield size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.overview.verifiedUsers.toLocaleString()}</div>
          <div className="stat-label">Verified ({stats.overview.verificationRate}%)</div>
        </div>
      </div>

      {/* Suspended Users */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#f59e0b' }}>
          <UserX size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.overview.suspendedUsers.toLocaleString()}</div>
          <div className="stat-label">Suspended</div>
        </div>
      </div>

      {/* Passengers */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#06b6d4' }}>
          <Users size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.byRole.passengers.toLocaleString()}</div>
          <div className="stat-label">Passengers</div>
        </div>
      </div>

      {/* Drivers */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#ec4899' }}>
          <Car size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.byRole.drivers.toLocaleString()}</div>
          <div className="stat-label">Drivers</div>
          {stats.drivers && (
            <div className="stat-sub">
              {stats.drivers.online} online
            </div>
          )}
        </div>
      </div>

      {/* Growth */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#14b8a6' }}>
          <TrendingUp size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.growth.newUsersThisMonth.toLocaleString()}</div>
          <div className="stat-label">New This Month</div>
          <div className="stat-sub">
            {stats.growth.newUsersThisWeek} this week
          </div>
        </div>
      </div>

      {/* Both Role */}
      <div className="stat-card">
        <div className="stat-icon" style={{ background: '#6366f1' }}>
          <Users size={24} />
        </div>
        <div className="stat-content">
          <div className="stat-value">{stats.byRole.both.toLocaleString()}</div>
          <div className="stat-label">Driver + Passenger</div>
        </div>
      </div>
    </div>
  );
};

export default UserStatsCards;